import { useState, useEffect, useMemo } from 'react';
import { nanoid } from "nanoid"

import Font from '../module/Font';
import { debounce } from 'lodash';
import styled from 'styled-components';
import { SlArrowDown, SlArrowUp } from "react-icons/sl";

import UbicationUser from '../utils/UbicationUser';
import { Group, Loader, Pagination, Space, Text, Title, useComputedColorScheme } from '@mantine/core';
import ControlTrad from '../assets/trad/ControlTrad';


const MainFontList = styled.div`
    display: flex;
    flex-direction: column;
    height: 65vh;
    overflow-y: auto;
`;


const MainFilter = styled.div`
    ${props => `
        display: flex;
        flex-direction: ${props.horizontal ? "row" : "column"};
        margin-top: 10px;
        align-items: ${props.horizontal ? "center" : ""};
        justify-content: center;
    `}
`;


const Select = styled.select`
    padding: 10px;
    margin-top: 5px;
    border: 1px solid #ccc;
    border-radius: 4px;
    font-size: 14px;
`;


const Paginated = styled.div`
    color: white;
    display: flex;
    justify-content: center;
`;

const LoadingScreen = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    height: 100vh;
    font-size: 24px;
`;

const MainFont = styled.div`
    ${props => `
        display: flex;
        flex-direction:row;
        justify-content: space-between;
        background-color: ${props.dark ? "#3b3b3b " : "#b5b5b5"};
        height: 50px;
        padding: 0 10px;
    `}
`;

const FontType = styled.p`
margin: 0;
align-content: center;
flex: 1;
width:10px;

@media (max-width: 425px) {
   display:none;
}
`;

const FontID = styled.p`
    ${props => `
margin: 0;
align-content: center;
flex: 2;
cursor: pointer;

&:hover{
    background-color: ${props.dark ? "#484848 " : "#cacaca"};
}

@media (max-width: 820px) {
   display:none;
}
    `}
`;

const FontTitle = styled.p`
    ${props => `

height: 50px;
margin: 0 3px;
align-content: center;
flex: 7;
cursor: pointer;
&:hover{
    background-color: ${props.dark ? "#484848 " : "#cacaca"};
}
    `}
`;

const FontDistance = styled.p`
    ${props => `
height: 50px;
margin: 0;
align-content: center;
flex: 3;
cursor: pointer;
&:hover{
    background-color: ${props.dark ? "#484848 " : "#cacaca"};
}
    `}
`;



const FontsList = (props) => {
    const { userLocation, error } = UbicationUser();
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [order, setOrder] = useState('asc');
    const [orderBy, setOrderBy] = useState('distance');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    const [loading, setLoading] = useState(true);
    const [width, setWidth] = useState(window.innerWidth);

    const computedColorScheme = useComputedColorScheme('light', { getInitialValueInEffect: true });


    const handleResize = () => {
        setWidth(window.innerWidth);
    };

    const changeOrder_OrderBy = (item) => {
        if (orderBy === item) {
            order === 'asc' ? setOrder('desc') : setOrder('asc');
        } else {
            setOrderBy(item);
            setOrder('asc');
        }
    }

    const haversine = (lat1, lon1, lat2, lon2) => {
        const toRadians = angle => (Math.PI / 180) * angle;
        const R = 6371;
        const dLat = toRadians(lat2 - lat1);
        const dLon = toRadians(lon2 - lon1);
        const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat2)) *
            Math.sin(dLon / 2) * Math.sin(dLon / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        return R * c;
    };


    useEffect(() => {
        window.addEventListener("resize", handleResize);
    }, []);


    useEffect(() => {
        const handler = debounce(() => setDebouncedSearchTerm(props.searchTerm), 500);
        handler();
        return () => {
            handler.cancel();
        };
    }, [props.searchTerm]);

    useEffect(() => {
        setLoading(true);
        const timer = setTimeout(() => {
            setLoading(false);
        }, 1000);
        return () => clearTimeout(timer);
    }, [debouncedSearchTerm, props?.filterType, props?.maxDistance, order, orderBy, page, pageSize]);

    const fontsWithDistance = useMemo(() => {
        if (!props.getAllFonts || !userLocation) return props.getAllFonts || [];
        return props.getAllFonts.map(font => ({
            ...font,
            distance: haversine(userLocation.lat, userLocation.lon, font.lat, font.lon),
        }));
    }, [props.getAllFonts, userLocation, order, orderBy]);

    const filteredFonts = useMemo(() => {
        return fontsWithDistance.filter(font => {
            return (
                (!props?.filterType || font.type === Number(props?.filterType)) &&
                (!debouncedSearchTerm ||
                    font.address.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
                    font.id.toUpperCase().includes(debouncedSearchTerm.toUpperCase())
                ) && (!props?.maxDistance || (font.distance * 1000) <= props?.maxDistance)
            );
        });
    }, [fontsWithDistance, props?.filterType, debouncedSearchTerm, props?.maxDistance], order, orderBy);

    const sortedFonts = useMemo(() => {
        return filteredFonts.sort((a, b) => {
            let comparison = 0;

            if (orderBy === 'address') {
                comparison = a.address.localeCompare(b.address);
            } else if (orderBy === 'code') {
                comparison = a.id.localeCompare(b.id);
            } else if (orderBy === 'distance' && userLocation) {
                comparison = a.distance - b.distance;
            }

            return order === 'asc' ? comparison : -comparison;
        });
    }, [filteredFonts, order, orderBy, userLocation]);

    const paginatedFonts = useMemo(() => {
        return sortedFonts.slice((page - 1) * pageSize, page * pageSize);
    }, [sortedFonts, page, pageSize]);

    if (loading) {
        return (

            <LoadingScreen>
                <Loader style={{ marginBottom: "25px" }} color="blue" size="xl" />
                <Title order={2} autoContrast> {ControlTrad.getTrad("loadFont")}</Title>
            </LoadingScreen>
        );
    }

    return (
        <div style={{ marginTop: "20px" }}>
            {error && <p>{error}</p>}
            {paginatedFonts.length > 0 ? (
                <>
                    <MainFont key={props.font?.id} dark={computedColorScheme === "dark"}>
                        <FontType></FontType>
                        <FontID dark={computedColorScheme === "dark"} onClick={() => { changeOrder_OrderBy("code") }}>{ControlTrad.getTrad("codi")} {orderBy === 'code' ? order === 'asc' ? <SlArrowUp /> : <SlArrowDown /> : <></>}</FontID>
                        <FontTitle dark={computedColorScheme === "dark"} onClick={() => { changeOrder_OrderBy("address") }}>{ControlTrad.getTrad("direccio")} {orderBy === 'address' ? order === 'asc' ? <SlArrowUp /> : <SlArrowDown /> : <></>} </FontTitle>
                        <FontDistance dark={computedColorScheme === "dark"} onClick={() => { changeOrder_OrderBy("distance") }}>{ControlTrad.getTrad("distancia")} {orderBy === 'distance' ? order === 'asc' ? <SlArrowUp /> : <SlArrowDown /> : <></>} </FontDistance>
                    </MainFont>
                    <MainFontList>
                        {paginatedFonts.map(font => (
                            <Font
                                key={nanoid()}
                                font={font}
                                distance={userLocation ? font.distance : 'Desconocida'}
                                admin={props.admin}
                            />

                        ))}
                    </MainFontList>
                    <Space h="xl" />
                    <Paginated>
                        <Pagination.Root value={page} onChange={setPage} total={Math.ceil(filteredFonts.length / pageSize)} >
                            <Group position="center">
                                <Pagination.Previous />
                                {width >= 450 ? <Pagination.Items /> : <div style={{ color: computedColorScheme === "dark" ? "white" : "#000" }}><Text autoContrast>{page} / {Math.ceil(filteredFonts.length / pageSize)}</Text></div>}
                                <Pagination.Next />
                            </Group>
                        </Pagination.Root>
                    </Paginated>
                    <MainFilter horizontal={true}>
                        <Text autoContrast style={{margin:"0 5px"}}>{ControlTrad.getTrad("tamanypa") + ": "}</Text>
                        <Select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}>
                            <option value={10}>10</option>
                            <option value={25}>25</option>
                        </Select>
                    </MainFilter>
                </>
            ) : (
                <Title order={3} style={{ textAlign: "center" }} autoContrast >{ControlTrad.getTrad("fontsListNotFound")}</Title >
            )}
        </div>
    );
};

export default FontsList;
